import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import type { RevenueMetrics, CategoryDistribution } from '@/lib/admin/analytics';

interface PriceAnalyticsChartProps {
  revenue: RevenueMetrics;
  categoryData: CategoryDistribution[];
}

const chartConfig = {
  averagePrice: {
    label: 'Average Price',
    color: 'hsl(217, 91%, 60%)',
  },
};

export function PriceAnalyticsChart({ revenue, categoryData }: PriceAnalyticsChartProps) {
  const chartData = categoryData.map((item) => ({
    category: item.category === '1bed' ? '1 Bedroom' : item.category === '2bed' ? '2 Bedroom' : 'Townhouse',
    'Average Price': item.averagePrice,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Price Analytics</CardTitle>
        <CardDescription>Average price by category</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer
          config={chartConfig}
          className="h-[300px] w-full"
        >
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" />
            <YAxis
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
            />
            <ChartTooltip
              content={<ChartTooltipContent />}
              formatter={(value: number) => [
                new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(value),
                'Average Price',
              ]}
            />
            <Bar dataKey="Average Price" fill="var(--color-averagePrice)" />
          </BarChart>
        </ChartContainer>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground">Overall Average</div>
            <div className="text-lg font-semibold">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(revenue.averagePrice)}
            </div>
          </div>
          {revenue.pricePerSqFt > 0 && (
            <div>
              <div className="text-muted-foreground">Price per Sq. Ft.</div>
              <div className="text-lg font-semibold">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(revenue.pricePerSqFt)}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

